import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { escapeHtmlText, openPrintWindow } from '@/lib/print-document';

/** POS-I406: what the slip needs from GET /sales/returns/:id. */
export interface RefundSlipData {
  returnNumber: string;
  returnType: string;
  status: string;
  totalRefund: number | string;
  notes?: string | null;
  createdAt: string;
  createdByName?: string | null;
  customerName?: string | null;
  refundMethods?: string[];
  fbrInvoiceId?: string | null;
  sale?: { label: string } | null;
  items: Array<{ productName: string; quantity: number; unitPrice: number | string; totalAmount: number | string }>;
}

const METHOD_LABELS: Record<string, string> = {
  CASH: 'Cash',
  CARD: 'Card',
  BANK_TRANSFER: 'Bank transfer',
  MOBILE_WALLET: 'Mobile wallet',
  CREDIT: 'Customer credit',
  CHEQUE: 'Cheque',
  GIFT_CARD: 'Gift card',
  QR_CODE: 'QR code',
};
const TYPE_LABELS: Record<string, string> = { CASH_REFUND: 'Refund', STORE_CREDIT: 'Store credit', EXCHANGE: 'Exchange' };

const SLIP_CSS =
  '.shop{font-size:15px;font-weight:700;margin-bottom:2px}table.items{width:100%;border-collapse:collapse;margin:10px 0}' +
  'table.items th,table.items td{padding:4px 6px;border-bottom:1px solid #ddd;text-align:left}table.items .r{text-align:right}' +
  '.total td{font-weight:700;border-top:2px solid #333}dl.meta{margin:6px 0}dl.meta div{display:flex;justify-content:space-between;gap:12px;padding:1px 0}' +
  'dl.meta dt{color:#666}dl.meta dd{margin:0;text-align:right}.sign{display:flex;justify-content:space-between;gap:24px;margin-top:40px}' +
  '.sign div{flex:1;border-top:1px solid #333;padding-top:4px;font-size:11px;color:#444;text-align:center}';

/** The slip HTML body. Pure (no network, no window) so it can be tested. */
export function buildRefundSlipBody(d: RefundSlipData, shopName: string): string {
  const e = escapeHtmlText;
  const methods = (d.refundMethods ?? []).map((m) => METHOD_LABELS[m] ?? m).join(', ');
  const meta: Array<[string, string]> = [
    ['Return no.', d.returnNumber],
    ['Original invoice', d.sale?.label ?? ''],
    ...(d.fbrInvoiceId ? ([['FBR invoice reference', d.fbrInvoiceId]] as Array<[string, string]>) : []),
    ['Date', formatDate(d.createdAt)],
    ['Refund type', TYPE_LABELS[d.returnType] ?? d.returnType],
    ['Refunded via', methods],
    ['Cashier', d.createdByName ?? ''],
    ['Customer', d.customerName ?? 'Walk-in'],
    ['Reason', d.notes ?? ''],
  ];
  const rows = d.items
    .map(
      (i) =>
        `<tr><td>${e(i.productName)}</td><td class="r">${e(String(i.quantity))}</td><td class="r">${e(formatCurrency(Number(i.unitPrice)))}</td><td class="r">${e(formatCurrency(Number(i.totalAmount)))}</td></tr>`,
    )
    .join('');
  return (
    (shopName ? `<div class="shop">${e(shopName)}</div>` : '') +
    `<dl class="meta">${meta.filter(([, v]) => v).map(([k, v]) => `<div><dt>${e(k)}</dt><dd>${e(v)}</dd></div>`).join('')}</dl>` +
    `<table class="items"><thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Price</th><th class="r">Amount</th></tr></thead><tbody>${rows}` +
    `<tr class="total"><td colspan="3">Total refunded</td><td class="r">${e(formatCurrency(Number(d.totalRefund)))}</td></tr></tbody></table>` +
    '<div class="sign"><div>Customer signature</div><div>Cashier signature</div></div>'
  );
}

/** Opens the printable refund slip / credit note. Returns false when the popup was blocked. */
export async function printRefundSlip(returnId: string): Promise<boolean> {
  const data = (await api.get<RefundSlipData>(`/sales/returns/${returnId}`)).data;
  let shopName = '';
  try {
    shopName = ((await api.get('/tenant')).data?.name as string | undefined) ?? '';
  } catch {
    /* the header falls back to no shop name */
  }
  return openPrintWindow({
    title: `Refund slip ${data.returnNumber}`,
    heading: data.returnType === 'CASH_REFUND' ? 'Refund slip / credit note' : `${TYPE_LABELS[data.returnType] ?? 'Return'} slip`,
    subtitle: `Printed ${new Date().toLocaleDateString()}`,
    bodyHtml: buildRefundSlipBody(data, shopName),
    extraCss: SLIP_CSS,
    width: 520,
    height: 720,
  });
}
